import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useTranslation } from 'react-i18next';
import { confirmHandler } from '../../../common/ConfirmModal';
import { PriceNumberFormat } from '../../../component/form/FormPriceInput';
import { DissolveValueDetail } from './OverlayDissolveDetail';
import { dashboardGridColumn, dashboardGridContainer } from './OverlayDissolveValues';
import { calculateSum, SumTable, SumTableEntryType } from './SumTable';
import { getValueWithVatCoefficientApplied } from './OverlayDissolveValues.utils';

export function OverlayDissolveDetailResidual({
  selectedDetail,
  setSelectedDetail,
  surcharge,
  vatCoefficient,
  onSave,
  removeSurcharge,
  requestApplication,
  residualSubsidy,
}: {
  selectedDetail: DissolveValueDetail;
  setSelectedDetail: (detail: DissolveValueDetail) => void;
  surcharge: number;
  vatCoefficient: number;
  onSave?: () => Promise<void>;
  removeSurcharge: boolean;
  requestApplication: (purchasePrice: number) => void;
  residualSubsidy: number;
}) {
  const { t } = useTranslation();

  const surchargeValue = selectedDetail.isSurchargeCancelled || removeSurcharge ? 0 : surcharge;

  const handleSurchageCancellation = () => {
    confirmHandler.confirm(
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchargeModalTitle'),
      () => {
        setSelectedDetail({ ...selectedDetail, isSurchargeCancelled: true });
      },
      () => {},
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchargeText'),
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchageConfirmButtonText'),
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchageCancelButtonText')
    );
  };

  const sumTableEntries = [
    {
      label: t('dashboard.contractsOverlay.dissolveValues.residualValueSum'),
      value: selectedDetail?.value,
      type: SumTableEntryType.Value,
    },
    {
      label: t('dashboard.contractsOverlay.dissolveValues.residualValueSubsidy'),
      value: residualSubsidy,
      type: SumTableEntryType.Subtraction,
    },
  ];

  if (!removeSurcharge) {
    sumTableEntries.push({
      label: t('dashboard.contractsOverlay.dissolveValues.surcharge'),
      value: surchargeValue,
      type: SumTableEntryType.Addition,
    });
  }

  if (selectedDetail?.addition) {
    sumTableEntries.push({
      label: t('dashboard.contractsOverlay.dissolveValues.addition'),
      value: selectedDetail.addition,
      type: SumTableEntryType.Addition,
    });
  }

  if (selectedDetail?.deduction) {
    sumTableEntries.push({
      label: t('dashboard.contractsOverlay.dissolveValues.deduction'),
      value: selectedDetail.deduction,
      type: SumTableEntryType.Subtraction,
    });
  }

  const sum = calculateSum(sumTableEntries);

  const sumWithoutSurcharge = sum - surchargeValue;

  sumTableEntries.push({
    label: t('dashboard.contractsOverlay.dissolveValues.dealerPurchasePrice'),
    value: sum,
    type: SumTableEntryType.Sum,
  });

  const displayedAddition = getValueWithVatCoefficientApplied(
    selectedDetail.addition,
    vatCoefficient
  );

  const displayedDeduction = getValueWithVatCoefficientApplied(
    selectedDetail.deduction,
    vatCoefficient
  );
  const onAdditionChange = (value: number) => {
    setSelectedDetail({ ...selectedDetail, addition: value / vatCoefficient });
  };

  const onDeductionChange = (value: number) => {
    setSelectedDetail({ ...selectedDetail, deduction: value / vatCoefficient });
  };

  return (
    <Grid container {...dashboardGridContainer} key={selectedDetail.type}>
      <Grid {...dashboardGridColumn}>
        <Stack gap={1}>
          <Typography variant="h5">
            {t('dashboard.contractsOverlay.dissolveValues.dissolveValueSum')}
          </Typography>
          <TextField
            value={displayedDeduction}
            label={t('dashboard.contractsOverlay.dissolveValues.deduction')}
            onChange={(e) => onDeductionChange(Number(e.target.value))}
            variant="standard"
            InputProps={{
              inputComponent: PriceNumberFormat,
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
          />
          <TextField
            value={displayedAddition}
            label={t('dashboard.contractsOverlay.dissolveValues.addition')}
            onChange={(e) => onAdditionChange(Number(e.target.value))}
            variant="standard"
            InputProps={{
              inputComponent: PriceNumberFormat,
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
          />
        </Stack>
      </Grid>
      <Grid {...dashboardGridColumn}>
        <Stack gap={1}>
          <Typography variant="h5">
            {t('dashboard.contractsOverlay.dissolveValues.purchasePriceSum')}
          </Typography>
          <SumTable entries={sumTableEntries} highlight vatCoefficient={vatCoefficient} />
          <Stack flexDirection="row-reverse" gap={2}>
            <Button variant="contained" onClick={() => requestApplication(sumWithoutSurcharge)}>
              {t('dashboard.contractsOverlay.dissolveValues.requestApplication')}
            </Button>
            {selectedDetail.surchargeCancellationPossible && !removeSurcharge && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSurchageCancellation}
                disabled={selectedDetail.isSurchargeCancelled}
              >
                {t('dashboard.contractsOverlay.dissolveValues.cancelSurcharge')}
              </Button>
            )}
            <Button variant="outlined" color="secondary" onClick={onSave}>
              {t('save')}
            </Button>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}
